import React, { useState, useEffect, useMemo } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	useToast,
	Spinner,
	Box,
	Flex,
	HStack,
	VStack,
	Text,
	Badge,
	Icon,
	IconButton,
	Skeleton,
} from '@chakra-ui/react';
import { FaCrown, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import { Elements } from '@stripe/react-stripe-js';
import getStripePromise from '../../../../utils/stripePromise';
import EmbeddedCheckout from './EmbeddedCheckout';
import { useAuthenticatedUserInfo } from '../../../../services/Contexts/AuthenticatedUserInfoContext';
import { useStripePrice } from '../../../../hooks/useStripe';
import { hasNeverHadSubscriptionPlan } from '../subscriptionPlanMatrixContext';

const stripePromise = getStripePromise();

const parseStripePrice = (stripePrice) => {
	if (!stripePrice) return null;

	let priceObj = null;
	if (
		stripePrice.data?.data &&
		typeof stripePrice.data.data === 'object' &&
		stripePrice.data.data.unit_amount != null
	) {
		priceObj = stripePrice.data.data;
	} else if (stripePrice.data?.unit_amount != null) {
		priceObj = stripePrice.data;
	} else if (stripePrice.unit_amount != null) {
		priceObj = stripePrice;
	} else {
		return null;
	}

	const unitAmount = priceObj.unit_amount;
	if (unitAmount == null || Number.isNaN(unitAmount)) return null;

	const amount = unitAmount / 100;
	const currency = (priceObj.currency || 'usd').toUpperCase();
	const symbol = currency === 'USD' ? '$' : `${currency} `;

	return {
		formatted: `${symbol}${amount.toLocaleString(undefined, {
			minimumFractionDigits: currency === 'USD' ? 0 : 2,
			maximumFractionDigits: currency === 'USD' ? 0 : 2,
		})}`,
		interval: priceObj.recurring?.interval,
	};
};

const SubscriptionModal = ({
	isOpen,
	onClose,
	plan,
	isYearly,
	onSubscriptionSuccess,
	currentSubscription,
	isUpgrade = false,
	deferSubscriptionRefresh = false,
}) => {
	const toast = useToast();
	const { userId, userEmail, companyId, token } = useAuthenticatedUserInfo();
	const [clientSecret, setClientSecret] = useState('');
	const [isLoadingSetupIntent, setIsLoadingSetupIntent] = useState(false);

	const stripePriceId = plan
		? isYearly
			? plan.stripePriceIdYearly
			: plan.stripePriceIdMonthly
		: null;
	const { data: stripePrice, isLoading: priceLoading } = useStripePrice(stripePriceId);
	const priceInfo = useMemo(() => parseStripePrice(stripePrice), [stripePrice]);

	const headerAccent = isUpgrade
		? { gradient: 'linear(135deg, purple.500 0%, pink.500 100%)', scheme: 'purple' }
		: { gradient: 'linear(135deg, teal.400 0%, cyan.500 100%)', scheme: 'teal' };

	const billingLabel = isYearly ? 'Annual billing' : 'Monthly billing';
	const priceSuffix = isYearly ? '/year' : '/month';
	const showMoneyBackGuarantee =
		!isUpgrade && hasNeverHadSubscriptionPlan(currentSubscription);

	useEffect(() => {
		if (!isOpen) {
			setClientSecret('');
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !plan || !companyId || !userEmail) return;

		const createSetupIntent = async () => {
			setIsLoadingSetupIntent(true);
			setClientSecret('');

			try {
				const endpoint = isUpgrade
					? '/api/stripe/customer-portal/create-upgrade-intent'
					: '/api/stripe/customer-portal/create-setup-intent';

				const requestBody = isUpgrade
					? {
							userEmail,
							companyId,
							userId,
							planPriceId: isYearly
								? plan.stripePriceIdYearly
								: plan.stripePriceIdMonthly,
							planName: plan.planName,
							currentSubscriptionId:
								currentSubscription?.subscriptionId ||
								currentSubscription?.paymentSubscriptionIntentId,
						}
					: {
							userEmail,
							companyId,
							userId,
							planPriceId: isYearly
								? plan.stripePriceIdYearly
								: plan.stripePriceIdMonthly,
							planName: plan.planName,
						};

				const response = await fetch(
					`${process.env.REACT_APP_API_URL}${endpoint}`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify(requestBody),
					},
				);

				const data = await response.json();

				if (!response.ok) {
					throw new Error(data.error || 'Failed to create setup intent');
				}

				setClientSecret(data.clientSecret);
			} catch (error) {
				console.error('Error creating setup intent:', error);
				toast({
					title: 'Error',
					description:
						error.message || 'Failed to initialize payment. Please try again.',
					status: 'error',
					duration: 5000,
					isClosable: true,
				});
			} finally {
				setIsLoadingSetupIntent(false);
			}
		};

		createSetupIntent();
	}, [
		isOpen,
		plan,
		companyId,
		userEmail,
		isYearly,
		isUpgrade,
		currentSubscription,
		userId,
		token,
		toast,
	]);

	const handleSuccess = (subscriptionData) => {
		toast({
			title: isUpgrade ? 'Plan updated successfully' : 'Welcome to the team!',
			description: isUpgrade
				? `You've successfully upgraded to ${plan.planName}`
				: `You've successfully subscribed to ${plan.planName}`,
			status: 'success',
			duration: 3000,
			isClosable: true,
		});

		setClientSecret('');
		onClose();

		if (onSubscriptionSuccess) {
			onSubscriptionSuccess(subscriptionData);
		}
	};

	const handleClose = () => {
		setClientSecret('');
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size={{ base: 'full', md: '4xl', lg: '5xl' }}
			isCentered
			scrollBehavior='inside'
			closeOnOverlayClick={!isLoadingSetupIntent}
			closeOnEsc={!isLoadingSetupIntent}>
			<ModalOverlay bg='blackAlpha.600' backdropFilter='blur(4px)' />
			<ModalContent
				mx={4}
				my={{ base: 0, md: 4 }}
				borderRadius={{ base: 0, md: '2xl' }}
				overflow='hidden'
				maxH={{ base: '100vh', md: '92vh' }}>
				{plan && (
					<Box
						bgGradient='linear(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
						px={6}
						py={6}
						color='white'
						position='relative'
						overflow='hidden'>
						<Box
							position='absolute'
							top='-50px'
							right='-50px'
							w='180px'
							h='180px'
							borderRadius='full'
							bg='whiteAlpha.100'
							pointerEvents='none'
						/>
						<Box
							position='absolute'
							bottom='-30px'
							left='35%'
							w='100px'
							h='100px'
							borderRadius='full'
							bg='whiteAlpha.50'
							pointerEvents='none'
						/>

						<Flex
							align={{ base: 'flex-start', md: 'center' }}
							justify='space-between'
							gap={4}
							position='relative'
							zIndex={1}
							pr={10}
							flexDirection={{ base: 'column', md: 'row' }}>
							<HStack spacing={4} align='flex-start' flex={1}>
								<Flex
									align='center'
									justify='center'
									w={12}
									h={12}
									borderRadius='xl'
									bgGradient={headerAccent.gradient}
									flexShrink={0}>
									<Icon as={FaCrown} boxSize={5} />
								</Flex>
								<Box>
									<HStack spacing={2} mb={1} flexWrap='wrap'>
										<Text
											fontSize='xs'
											color='whiteAlpha.700'
											fontWeight='600'
											textTransform='uppercase'
											letterSpacing='0.12em'
											m={0}>
											Secure checkout
										</Text>
										<Badge
											colorScheme={headerAccent.scheme}
											borderRadius='full'
											px={2}
											fontSize='10px'>
											{isUpgrade ? 'Plan change' : 'New subscription'}
										</Badge>
									</HStack>
									<Text
										as='h2'
										fontSize={{ base: 'xl', md: '2xl' }}
										fontWeight='800'
										lineHeight='1.2'
										m={0}
										mb={2}>
										{isUpgrade ? 'Upgrade to' : 'Subscribe to'} {plan.planName}
									</Text>
									<HStack spacing={3} flexWrap='wrap'>
										<Badge
											bg='whiteAlpha.200'
											color='white'
											borderRadius='full'
											px={3}
											py={1}
											fontSize='xs'>
											{billingLabel}
										</Badge>
										{priceLoading ? (
											<Skeleton height='20px' width='80px' />
										) : priceInfo ? (
											<Text fontSize='sm' color='whiteAlpha.900' m={0} fontWeight='600'>
												{priceInfo.formatted}
												<Text as='span' color='whiteAlpha.700' fontWeight='500'>
													{priceSuffix}
												</Text>
											</Text>
										) : null}
									</HStack>
								</Box>
							</HStack>

							{showMoneyBackGuarantee ? (
								<Box
									display={{ base: 'none', md: 'block' }}
									px={4}
									py={3}
									borderRadius='xl'
									bg='whiteAlpha.100'
									border='1px solid'
									borderColor='whiteAlpha.200'
									flexShrink={0}>
									<HStack spacing={2}>
										<Icon as={FaCheckCircle} color='green.300' boxSize={4} />
										<Text fontSize='xs' color='whiteAlpha.900' m={0} maxW='200px' lineHeight='1.4'>
											30-day money-back guarantee on your first subscription
										</Text>
									</HStack>
								</Box>
							) : null}
						</Flex>

						<IconButton
							aria-label='Close'
							icon={
								<Text fontSize='lg' lineHeight={1}>
									×
								</Text>
							}
							position='absolute'
							top={3}
							right={3}
							variant='ghost'
							color='white'
							_hover={{ bg: 'whiteAlpha.200' }}
							onClick={handleClose}
							isDisabled={isLoadingSetupIntent}
						/>
					</Box>
				)}

				<ModalBody px={{ base: 4, md: 6 }} py={5}>
					{showMoneyBackGuarantee ? (
						<Box
							display={{ base: 'flex', md: 'none' }}
							mb={4}
							p={3}
							borderRadius='lg'
							bg='green.50'
							border='1px solid'
							borderColor='green.100'
							alignItems='center'
							gap={2}>
							<Icon as={FaCheckCircle} color='green.500' flexShrink={0} />
							<Text fontSize='xs' color='green.800' m={0}>
								30-day money-back guarantee on your first subscription
							</Text>
						</Box>
					) : null}

					{isLoadingSetupIntent || !clientSecret ? (
						<VStack spacing={5} py={8} align='stretch'>
							<HStack justify='center' spacing={3}>
								<Spinner size='lg' color='teal.500' thickness='3px' />
								<VStack align='start' spacing={0}>
									<Text fontWeight='600' color='gray.700' m={0}>
										Preparing secure checkout
									</Text>
									<Text fontSize='sm' color='gray.500' m={0}>
										Connecting to Stripe…
									</Text>
								</VStack>
							</HStack>
							<VStack spacing={3} align='stretch'>
								<Skeleton height='48px' borderRadius='lg' />
								<Skeleton height='120px' borderRadius='lg' />
								<Skeleton height='48px' borderRadius='lg' />
							</VStack>
						</VStack>
					) : (
						<Elements
							stripe={stripePromise}
							options={{
								clientSecret,
								appearance: {
									theme: 'stripe',
									variables: {
										colorPrimary: isUpgrade ? '#805AD5' : '#319795',
										borderRadius: '8px',
									},
								},
							}}>
							<EmbeddedCheckout
								plan={plan}
								isYearly={isYearly}
								onSuccess={handleSuccess}
								onCancel={handleClose}
								isUpgrade={isUpgrade}
								currentSubscription={currentSubscription}
								showSuccessToast={false}
								deferSubscriptionRefresh={deferSubscriptionRefresh}
								embeddedInModal
								showMoneyBackGuarantee={showMoneyBackGuarantee}
							/>
						</Elements>
					)}

					{!isLoadingSetupIntent && clientSecret && (
						<HStack spacing={2} justify='center' color='gray.400' pt={4}>
							<Icon as={FaShieldAlt} boxSize={3} />
							<Text fontSize='xs' m={0}>
								Payments secured by Stripe · Cancel anytime from billing settings
							</Text>
						</HStack>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default SubscriptionModal;
